import type { ShippingMethod, DeliveryLeadTime } from '@commercelayer/sdk'

/**
 * Represents detailed delivery information for a shipping method
 *
 * @typedef {Object} DeliveryInformation
 * @property {string} id - Unique identifier for the shipping method
 * @property {string} type - Name or type of shipping method
 * @property {number} costFloat - Shipping cost as a floating-point number
 * @property {string} cost - Formatted shipping cost (can be "Free Shipping")
 * @property {string} description - Delivery time description
 * @property {string} [pdpDescription] - Product detail page shipping description
 * @property {number} [free_over_amount_float] - Minimum order amount for free shipping
 * @property {boolean} [use_subtotal] - Whether subtotal is used for free shipping calculation
 * @property {string} [reference] - Additional reference for the shipping method
 * @property {Object} [deliveryLeadTimes] - Minimum and maximum delivery days
 * @property {number} deliveryLeadTimes.min_days - Minimum delivery days
 * @property {number} deliveryLeadTimes.max_days - Maximum delivery days
 */
export type DeliveryInformation = {
  id: string
  type: string
  cost: string
  costFloat: number
  description: string
  trackOrderDescription?: string
  pdpDescription?: string
  free_over_amount_float?: number | null
  use_subtotal?: boolean | null
  reference?: string | null
  deliveryLeadTimes?: {
    min_days: number
    max_days: number
  } | null
}

/**
 * Composable for managing shipping methods and delivery information
 *
 * Provides functionality to fetch, filter, and process shipping methods
 *
 * @returns {Object} Shipping methods related utilities
 * @property {ComputedRef<boolean>} pickUpInStoreEnabled - Whether pickup in store is available
 * @property {ComputedRef<DeliveryInformation[]>} marketShippingMethods - Available shipping methods for current market
 * @property {ComputedRef<DeliveryInformation>} marketDefaultShippingMethod - Default shipping method
 * @property {Function} fetchShippingMethodsByMarket - Fetch shipping methods for a specific market
 *
 * @example
 * const {
 *   pickUpInStoreEnabled,
 *   marketShippingMethods,
 *   marketDefaultShippingMethod,
 *   fetchShippingMethodsByMarket
 * } = useShippingMethods()
 */
export const useShippingMethods = () => {
  const { formatPriceByLocale } = useFormatPrice()
  const { ts } = useI18n()
  const configs = useConfigs()
  const market = computed(() => configs.value.cl_market_code!)

  /**
   * Type definition for shipping methods response
   * @typedef {Array<ShippingMethod & { deliveryLeadTimes: DeliveryLeadTime[] | null }>} ShippingMethodsResponse
   */
  type ShippingMethodsResponse = (ShippingMethod & {
    deliveryLeadTimes: DeliveryLeadTime[] | null
  })[]

  /**
   * Maps raw shipping methods to a more user-friendly delivery information format
   *
   * @param {ShippingMethodsResponse} shippingMethods - Raw shipping methods from API
   * @returns {DeliveryInformation[]} Processed and formatted delivery information
   */
  const mapShippingMethods = (
    shippingMethods: ShippingMethodsResponse
  ): DeliveryInformation[] => {
    return shippingMethods
      .filter(shippingMethod => shippingMethod.deliveryLeadTimes?.length)
      .map(shippingMethod => {
        const delivery = shippingMethod.deliveryLeadTimes![0]
        return {
          id: shippingMethod?.id ?? '',
          type: shippingMethod?.name ?? '',
          costFloat: shippingMethod?.price_amount_float ?? 0,
          cost:
            shippingMethod?.price_amount_float === 0
              ? ts('cartPage.recap.sideSheet.shipping.freeShipping')
              : formatPriceByLocale(
                  shippingMethod?.currency_code ?? '',
                  shippingMethod?.price_amount_float ?? 0
                ),
          description:
            delivery.min_days && delivery.max_days
              ? ts('cartPage.recap.sideSheet.shipping.deliveryIn', {
                  min: delivery.min_days,
                  max: delivery.max_days,
                })
              : ts('cartPage.recap.sideSheet.shipping.delivery'),
          trackOrderDescription:
            delivery.min_days && delivery.max_days
              ? ts('trackOrder.shipping', {
                  min: delivery.min_days,
                  max: delivery.max_days,
                })
              : ts('trackOrder.shippingOneDay'),
          pdpDescription:
            delivery.min_days && delivery.max_days
              ? ts('pdp.shipping', {
                  min: delivery.min_days,
                  max: delivery.max_days,
                })
              : ts('pdp.shippingOneDay'),
          free_over_amount_float: shippingMethod.free_over_amount_float,
          use_subtotal: shippingMethod.use_subtotal,
          reference: shippingMethod.reference,
          deliveryLeadTimes:
            delivery?.min_days && delivery?.max_days
              ? {
                  min_days: delivery.min_days,
                  max_days: delivery.max_days,
                }
              : null,
        }
      })
  }

  /**
   * Lazy fetch for shipping methods based on current market
   *
   * @type {Ref<ShippingMethodsResponse>}
   */
  const { data } = useLazyFetch<ShippingMethodsResponse>(
    `/api/cl/getShippingMethods/${market.value}`,
    {
      key: `useShippingMethods-marketShippingMethods-${market.value}`,
      getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
      default: () => [],
      dedupe: 'defer',
    }
  )

  /**
   * Fetches shipping methods for a specific market
   *
   * @param {string} market - Market code to fetch shipping methods for
   * @returns {Promise<DeliveryInformation[]>} Processed shipping methods
   */
  const fetchShippingMethodsByMarket = async (market: string) => {
    const methods = await $fetch<ShippingMethodsResponse>(
      `/api/cl/getShippingMethods/${market}`
    )
    return methods ? mapShippingMethods(methods) : []
  }

  /**
   * Computed property to check if pickup in store is enabled
   *
   * @type {ComputedRef<boolean>}
   */
  const pickUpInStoreEnabled = computed(
    () => data.value?.some(method => method.metadata?.pickupinstore) ?? true
  )

  /**
   * Computed property for shipping methods in the current market
   *
   * @type {ComputedRef<DeliveryInformation[]>}
   */
  const marketShippingMethods = computed(() =>
    mapShippingMethods(data.value ?? [])
  )

  /**
   * Computed property for the default shipping method
   * Prioritizes "Standard" shipping method, falls back to first available method
   *
   * @type {ComputedRef<DeliveryInformation>}
   */
  const marketDefaultShippingMethod = computed<DeliveryInformation>(
    () =>
      marketShippingMethods.value.find(method =>
        method.type.includes('Standard')
      ) ||
      marketShippingMethods?.value[0] || {
        id: '',
        type: '',
        cost: '',
        costFloat: 0,
        description: '',
      }
  )

  return {
    pickUpInStoreEnabled,
    marketShippingMethods,
    marketDefaultShippingMethod,
    fetchShippingMethodsByMarket,
  }
}
